import {
    json,
    redirect,
    type ActionFunctionArgs,
    type LinksFunction,
} from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { type HDEPageData } from '@modules/hde/loaders/get-hde-page-loader.server';
import {
    submitMGEForm,
    type MGEFormInput,
} from '@modules/hde/actions/submit-mge-form.server';
import hdeStyles from '@modules/hde/styles/hde.css?url';

import { HeroBanner } from '~/modules/hde/HeroBanner';
import { EventIntro } from '~/modules/hde/EventIntro';
import { TheEvening } from '~/modules/hde/TheEvening';
import { HowItWorks } from '~/modules/hde/HowItWorks';
import { RunSheet } from '~/modules/hde/RunSheet';
import { PanelSpotlight } from '~/modules/hde/PanelSpotlight';
import { ExhibitorBanner } from '~/modules/hde/ExhibitorBanner';
import { ExhibitorGrid } from '~/modules/hde/ExhibitorGrid';
import { HDEExhibitorLogoGrid } from '~/modules/hde/HDEExhibitorLogoGrid';
import { ExhibitorHighlights } from '~/modules/hde/ExhibitorHighlights';
import { RegistrationValue } from '~/modules/hde/RegistrationValue';
import { FAQ } from '~/modules/hde/FAQ';
import { ClosingCTA } from '~/modules/hde/ClosingCTA';
import { getHDERouteLoaderData } from '~/modules/hde/loaders/get-hde-route-loader.server';
import type { APHandle } from '~/modules/root/type';
import type { HDEAssets } from '~/modules/hde/types';
import {
    HDERegistrationModal,
    useHDERegistrationModal,
} from '~/modules/hde/components/registration';
import { HDE_PAGE_PATH } from '~/modules/hde/constants';
import {
    createMetaDescription,
    createMetaTitle,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';

/** Normalise empty or invalid phone/string values so we never pass "undefined" to the backend. */
function normalizeOptionalString(value: unknown): string | undefined {
    if (value == null) return undefined;
    const s = String(value).trim();
    if (s === '' || s === 'undefined' || s === 'null') return undefined;
    return s;
}

/** Temporary asset URLs — replace with S3 links once exported. */
const assets: HDEAssets = {
    heroVideo: '/assets/website/hde_2026/HDE_hero_optimised.mp4',
    theEvening: {
        photo1: '/assets/website/hde_2026/the_evening_1.png',
        photo2: '/assets/website/hde_2026/the_evening_2.png',
    },
    howItWorks: {
        photo1: '/assets/website/hde_2026/browse_products.png',
        photo2: '/assets/website/hde_2026/discover_pro.png',
    },
    runSheet: {
        sideImage: '/assets/website/hde_2026/tom_webser.png',
    },
    panelSpotlight: {
        jeremyBull: '/assets/website/hde_2026/jeremy_bull.png',
        joshFitzgerald: '/assets/website/hde_2026/josh_fitzgerald.png',
        publicPanel: {
            oliverTuck: '/assets/website/hde_2026/oliver_tuck.png',
            oliBooth: '/assets/website/hde_2026/oli_booth.png',
            dahliaGhani1: '/assets/website/hde_2026/oli_booth.png',
            dahliaGhani2: '/assets/website/hde_2026/oliver_tuck.png',
        },
    },
    exhibitorBanner: '/assets/website/hde_2026/HDE_banner.mp4',
    exhibitors: [
        {
            name: 'Altus Window Systems',
            title: 'Altus Window Systems',
            image: '/assets/website/hde_2026/Altus_window_systems.png',
        },
        {
            name: 'FORTÉ',
            title: 'FORTÉ',
            image: '/assets/website/hde_2026/FORTE.png',
        },
        {
            name: 'BESPOKE TIMBER DESIGN',
            title: 'BESPOKE TIMBER DESIGN',
            image: '/assets/website/hde_2026/BESPOKE_TIMBER_DESIGN.png',
        },
        {
            name: 'NERO TAPWARE',
            title: 'NERO TAPWARE',
            image: '/assets/website/hde_2026/NERO.png',
        },
        {
            name: 'QUANTUM',
            title: 'QUANTUM',
            image: '/assets/website/hde_2026/QUANTUM.png',
        },
        {
            name: 'FL BONE',
            title: 'FL BONE',
            image: '/assets/website/hde_2026/FL_BONE.png',
        },
        {
            name: 'THE NATURAL STONE CO.',
            title: 'THE NATURAL STONE CO.',
            image: '/assets/website/hde_2026/ISLAND_STONE.png',
        },
        {
            name: 'HANSEN TIMBER',
            title: 'HANSEN TIMBER',
            image: '/assets/website/hde_2026/HANSEN_TIMBER.png',
        },
        {
            name: 'KITCHEN ARCHITECTURE',
            title: 'KITCHEN ARCHITECTURE',
            image: '/assets/website/hde_2026/KITCHEN_ARCHITECTURE.png',
        },
        {
            name: 'ARTISAN STONE',
            title: 'ARTISAN STONE',
            image: '/assets/website/hde_2026/artisan_stone.png',
        },
        {
            name: 'VIDASPACE',
            title: 'VIDASPACE',
            image: '/assets/website/hde_2026/VIDASPACE.png',
        },
    ],
    registrationValue: {
        beforeEvening: '/assets/website/hde_2026/value_01.png',
        onTheNight: '/assets/website/hde_2026/value_02.png',
        afterEvening: '/assets/website/hde_2026/value_03.png',
    },
    closingCTA: {
        heroImage: '/assets/website/hde_2026/footer_gallery.png',
    },
};

export const links: LinksFunction = () => [
    { rel: 'stylesheet', href: hdeStyles },
    {
        rel: 'preload',
        href: assets.heroVideo,
        as: 'video',
        type: 'video/mp4',
    },
];

export const loader = async (
    args: Parameters<typeof getHDERouteLoaderData>[0],
) => {
    return getHDERouteLoaderData(args, assets);
};

export const action = async (args: ActionFunctionArgs) => {
    const formData = await args.request.formData();

    // Parse BuildHubID if provided
    const buildHubIdStr = formData.get('BuildHubID') as string | null;
    const buildHubId = buildHubIdStr ? Number(buildHubIdStr) : undefined;

    const input: MGEFormInput = {
        ApplicationType: formData.get('ApplicationType') as string,
        EventIDs: JSON.parse((formData.get('EventIDs') as string) || '[]'),
        TimeSlot: (formData.get('TimeSlot') as string) || undefined,
        FirstName: (formData.get('FirstName') as string) || undefined,
        LastName: (formData.get('LastName') as string) || undefined,
        Email: (formData.get('Email') as string) || undefined,
        Phone: normalizeOptionalString(formData.get('Phone')),
        BringingGuest: formData.get('BringingGuest') === 'true',
        GuestEmail: (formData.get('GuestEmail') as string) || undefined,
        GuestFirstName: (formData.get('GuestFirstName') as string) || undefined,
        GuestLastName: (formData.get('GuestLastName') as string) || undefined,
        GuestPhone: normalizeOptionalString(formData.get('GuestPhone')),
        HearAboutMGE: (formData.get('HearAboutMGE') as string) || undefined,
        ProjectArchitect:
            (formData.get('ProjectArchitect') as string) || undefined,
        ProjectAttendanceReason:
            (formData.get('ProjectAttendanceReason') as string) || undefined,
        ProjectBudget: (formData.get('ProjectBudget') as string) || undefined,
        ProjectLocation:
            (formData.get('ProjectLocation') as string) || undefined,
        ProjectLocationPostcode:
            (formData.get('ProjectLocationPostcode') as string) || undefined,
        ProjectProductInterest:
            (formData.get('ProjectProductInterest') as string) || undefined,
        ProjectStage: (formData.get('ProjectStage') as string) || undefined,
        ProjectType: (formData.get('ProjectType') as string) || undefined,
        ProjectScope: (formData.get('ProjectScope') as string) || undefined,
        PropertyOwned: formData.get('PropertyOwned') === 'true',
        PropertySize: (formData.get('PropertySize') as string) || undefined,
        CompanyName: (formData.get('CompanyName') as string) || undefined,
        CompanyWebsite: (formData.get('CompanyWebsite') as string) || undefined,
        CompanyType: (formData.get('CompanyType') as string) || undefined,
        CompanyLocation:
            (formData.get('CompanyLocation') as string) || undefined,
        HDEOtherPleaseSpecify:
            (formData.get('HDEOtherPleaseSpecify') as string) || undefined,
        BuildHubID: buildHubId,
        HDEProjectTitle:
            (formData.get('HDEProjectTitle') as string) || undefined,
        HDEBuildHubProjectType:
            (formData.get('HDEBuildHubProjectType') as string) || undefined,
        HDEBuildHubProjectScope:
            (formData.get('HDEBuildHubProjectScope') as string) || undefined,
        HDEBuildHubActiveStageURLSegment:
            (formData.get('HDEBuildHubActiveStageURLSegment') as string) ||
            undefined,
        HDEBuildHubBudgetMin: formData.get('HDEBuildHubBudgetMin')
            ? Number(formData.get('HDEBuildHubBudgetMin'))
            : undefined,
        HDEBuildHubBudgetMax: formData.get('HDEBuildHubBudgetMax')
            ? Number(formData.get('HDEBuildHubBudgetMax'))
            : undefined,
        HDEBuildHubLocationData:
            (formData.get('HDEBuildHubLocationData') as string) || undefined,
        AutoCreateMember: formData.get('AutoCreateMember') === 'true',
    };

    const result = await submitMGEForm(args, input);

    if (result.success) {
        const isModalSubmission = formData.get('IsModalSubmission') === 'true';
        if (isModalSubmission) {
            return json({ success: true });
        }
        return redirect(HDE_PAGE_PATH);
    }

    return json({ errors: result.errors });
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;
        const pageData = data?.pageData as HDEPageData | undefined;
        const metaData = pageData?.meta;
        const pageTitle = metaData?.Title?.includes('Home Design Evening')
            ? metaData.Title
            : 'Home Design Evening';

        const title = createMetaTitle(props, [pageTitle, 'ArchiPro']);
        const description = createMetaDescription([
            metaData?.MetaDescription ?? '',
        ]);

        return [
            {
                title,
            },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
                facebookSocialImage:
                    metaData?.SocialPreviewImage ??
                    'https://archipro.co.nz/_resources/themes/archipro2/images/ace/ace-event-image.jpg',
            }),
            ...getCanonicalHreflang(props),
        ];
    },
);

export const handle: APHandle = {
    hideFooter: {
        desktop: true,
        tablet: true,
        mobile: true,
    },
    isChatBoxesDisabled: true,
    disableMobileUserScaling: true,
};

const HDERoute = () => {
    const { openModal } = useHDERegistrationModal();
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const {
        assets,
        pageData,
        hasActiveEvent,
        registerLocationOptions,
        primaryEventDateLabel,
        primaryEventLocationLabel,
        primaryEventLocationLineLabel,
        presentedByLogoUrl,
        presentedByProfilePath,
        presentedBySponsorName,
        runSheetTimeSlots,
        exhibitorLogos,
        exhibitorHighlights,
        exhibitorHighlightsTitle,
        panelSpeakers,
        panelDisplayTitle,
        panelDescription,
        publicPanelSpeakers,
        publicPanelDisplayTitle,
        publicPanelDescription,
        hdeInPageBanner,
        hdeGridCarousel,
        hdeExhibitorProfileGrid,
    } = useLoaderData<typeof loader>();
    const showRegisterButton =
        hasActiveEvent && registerLocationOptions.length > 0;

    const exhibitorsForGrid =
        hdeExhibitorProfileGrid && hdeExhibitorProfileGrid.length > 0
            ? hdeExhibitorProfileGrid
            : assets.exhibitors;

    const handleRegisterClick = () => {
        if (!showRegisterButton) {
            return;
        }
        openModal(registerLocationOptions);
    };

    return (
        <div className="w-full">
            <HeroBanner
                videoSrc={assets.heroVideo}
                onRegister={handleRegisterClick}
                eventDateText={primaryEventDateLabel}
                eventLocationText={primaryEventLocationLabel}
                showRegisterButton={showRegisterButton}
                isMobile={isMobile}
                showPresentedBySponsor={hasActiveEvent}
                presentedByLogoUrl={presentedByLogoUrl}
                presentedByProfilePath={presentedByProfilePath}
                presentedBySponsorName={presentedBySponsorName}
            />
            <EventIntro
                onRegister={handleRegisterClick}
                showRegisterButton={showRegisterButton}
                isMobile={isMobile}
            />
            <TheEvening
                assets={assets.theEvening}
                isMobile={isMobile}
                onRegister={handleRegisterClick}
                showRegisterButton={showRegisterButton}
            />
            <HowItWorks assets={assets.howItWorks} isMobile={isMobile} />
            <RunSheet
                sideImage={assets.runSheet.sideImage}
                onRegister={handleRegisterClick}
                showRegisterButton={showRegisterButton}
                timeSlots={runSheetTimeSlots}
                isMobile={isMobile}
                showPresentedBySponsor={hasActiveEvent}
                presentedByLogoUrl={presentedByLogoUrl}
                presentedByProfilePath={presentedByProfilePath}
                presentedBySponsorName={presentedBySponsorName}
            />
            <PanelSpotlight
                panelSpeakers={panelSpeakers}
                panelDisplayTitle={panelDisplayTitle}
                panelDescription={panelDescription}
                publicPanelSpeakers={publicPanelSpeakers}
                publicPanelDisplayTitle={publicPanelDisplayTitle}
                publicPanelDescription={publicPanelDescription}
                onRegister={handleRegisterClick}
                showRegisterButton={showRegisterButton}
                isMobile={isMobile}
            />
            <ExhibitorBanner
                videoSrc={hdeInPageBanner?.videoSrc ?? assets.exhibitorBanner}
                isMobile={isMobile}
                headlineLine1={hdeInPageBanner?.headlineLine1}
                headlineLine2={hdeInPageBanner?.headlineLine2}
                bodyDesktop={hdeInPageBanner?.bodyDesktop}
                bodyMobile={hdeInPageBanner?.bodyMobile}
            />
            <ExhibitorGrid
                exhibitors={exhibitorsForGrid}
                onRegister={handleRegisterClick}
                showRegisterButton={showRegisterButton}
                isMobile={isMobile}
                sectionHeadingDesktop={hdeGridCarousel?.headingDesktop}
                sectionHeadingMobile={hdeGridCarousel?.headingMobile}
                meetLabel={hdeGridCarousel?.meetLabel}
                atLabel={hdeGridCarousel?.atLabel}
                eventWordOne={hdeGridCarousel?.eventWordOne}
                eventWordTwo={hdeGridCarousel?.eventWordTwo}
                eventWordThree={hdeGridCarousel?.eventWordThree}
            />
            <HDEExhibitorLogoGrid logos={exhibitorLogos} isMobile={isMobile} />
            <ExhibitorHighlights
                cards={exhibitorHighlights}
                displayTitle={exhibitorHighlightsTitle}
                isMobile={isMobile}
            />
            <RegistrationValue
                assets={assets.registrationValue}
                onRegister={handleRegisterClick}
                showRegisterButton={showRegisterButton}
                isMobile={isMobile}
            />
            <FAQ isMobile={isMobile} />
            <ClosingCTA
                heroImage={assets.closingCTA.heroImage}
                onRegister={handleRegisterClick}
                eventDateText={primaryEventDateLabel}
                eventLocationText={primaryEventLocationLineLabel}
                showRegisterButton={showRegisterButton}
                isMobile={isMobile}
                showPresentedBySponsor={hasActiveEvent}
                presentedByLogoUrl={presentedByLogoUrl}
                presentedByProfilePath={presentedByProfilePath}
                presentedBySponsorName={presentedBySponsorName}
            />
            <HDERegistrationModal
                modules={pageData.modules as HDEPageData['modules']}
            />
        </div>
    );
};

export default HDERoute;
